import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useShop } from '../../contexts/ShopContext';
import ShopSplashScreen from '../../components/shop/ShopSplashScreen';
import ShopTopBar from '../../components/shop/ShopTopBar';
import ShopBottomNav from '../../components/shop/ShopBottomNav';
import { statusLabel, statusColor } from '../../utils/job-status';
import JobQueueCard from '../../components/shop/JobQueueCard';
import ActiveJobCard from '../../components/shop/ActiveJobCard';
import {
  Search, Hammer, Clock, Archive, PlayCircle, CheckCircle2, AlertTriangle, Inbox, X,
} from 'lucide-react';

// Warm palette — same family used across JobApprovalModal / JobDetailView / the
// Estimates flow.
const INK    = '#2B2620';
const WALNUT = '#8B4513';
const MOSS   = '#3B6B44';
const OCHRE  = '#A6752A';
const RUST   = '#A13D2D';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';
const LINE   = 'rgba(43, 38, 32, 0.09)';

const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

function EmptyState({ Icon, title, subtitle }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: '16px', border: `1px solid ${LINE}` }}>
      <Icon size={30} strokeWidth={1.5} color={STONE} style={{ marginBottom: '12px', opacity: 0.7 }} />
      <div style={{ fontFamily: FONT_DISPLAY, fontSize: '18px', fontWeight: '600', color: INK, marginBottom: '6px' }}>{title}</div>
      <div style={{ fontSize: '14px', color: STONE }}>{subtitle}</div>
    </div>
  );
}

function SectionHeader({ Icon, title, count, countLabel, accent }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
      <Icon size={19} strokeWidth={2.25} color={accent} />
      <div style={{ fontFamily: FONT_DISPLAY, fontSize: '20px', fontWeight: '600', color: INK }}>{title}</div>
      <div style={{ padding: '4px 10px', background: accent + '18', borderRadius: '8px', fontSize: '12px', fontWeight: '700', color: accent }}>
        {count} {countLabel}
      </div>
    </div>
  );
}

function ConfirmationModal({ job, onConfirm, onCancel }) {
  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 3000,
        background: 'rgba(30, 25, 20, 0.62)', backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px', fontFamily: FONT_SANS, animation: 'twpShopFade 0.2s ease-out',
      }}
    >
      <style>{`
        @keyframes twpShopFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes twpShopRise { from { opacity: 0; transform: translateY(16px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '460px', background: PAPER, borderRadius: '20px',
          border: `1px solid ${LINE}`, boxShadow: '0 30px 80px rgba(30, 25, 20, 0.35)',
          animation: 'twpShopRise 0.28s cubic-bezier(0.2, 0.9, 0.3, 1.05)', overflow: 'hidden'
        }}
      >
        <div style={{ height: '3px', background: WALNUT }} />

        <div style={{ padding: '24px 26px 4px', position: 'relative' }}>
          <button onClick={onCancel} aria-label="Close" style={{
            position: 'absolute', top: '16px', right: '16px', width: '28px', height: '28px',
            borderRadius: '8px', border: 'none', background: 'transparent', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: STONE,
          }}>
            <X size={16} strokeWidth={2} />
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
            <div style={{
              width: '44px', height: '44px', borderRadius: '12px', background: `${WALNUT}14`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <PlayCircle size={22} strokeWidth={2} color={WALNUT} />
            </div>
            <div>
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: '21px', fontWeight: '600', color: INK }}>Start Production?</div>
              <div style={{ fontSize: '13px', color: STONE }}>You&apos;re about to begin building this job</div>
            </div>
          </div>
        </div>

        <div style={{ padding: '4px 26px 24px' }}>
          <div style={{ background: 'rgba(43,38,32,0.04)', borderRadius: '12px', padding: '14px 16px', marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: STONE, marginBottom: '6px' }}>Job Details</div>
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: '20px', fontWeight: '600', color: WALNUT, marginBottom: '2px' }}>#{job.contractNumber || job.id?.slice(0, 6)}</div>
            <div style={{ fontSize: '15px', color: INK, fontWeight: '600' }}>{job.customerName || job.name || 'Customer'}</div>
          </div>

          <div style={{ padding: '13px 15px', background: `${RUST}0d`, borderRadius: '11px', border: `1px solid ${RUST}33`, marginBottom: '22px' }}>
            <div style={{ display: 'flex', gap: '7px', alignItems: 'center', fontSize: '12.5px', fontWeight: '600', color: RUST, marginBottom: '5px' }}>
              <AlertTriangle size={13} strokeWidth={2.5} /> Heads up
            </div>
            <div style={{ fontSize: '12.5px', color: INK, lineHeight: '1.5' }}>
              This updates the job status and notifies managers — the customer is also notified their project moved forward. Only proceed if production is actually starting now.
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <button onClick={onCancel} style={{
              padding: '13px', background: '#fff', border: `1.5px solid ${LINE}`, borderRadius: '11px',
              color: STONE, fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT_SANS,
            }}>Cancel</button>
            <button onClick={onConfirm} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
              padding: '13px', background: WALNUT, border: 'none', borderRadius: '11px',
              color: 'white', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: FONT_SANS,
              boxShadow: `0 4px 14px ${WALNUT}48`,
            }}>
              <PlayCircle size={16} strokeWidth={2.25} /> Start Production
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SuccessModal({ job, customer }) {
  return (
    <div style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 4000, animation: 'twpShopSlideDown 0.3s ease-out', fontFamily: FONT_SANS }}>
      <div style={{
        background: PAPER, borderRadius: '16px', padding: '16px 22px', boxShadow: '0 8px 24px rgba(30,25,20,0.25)',
        border: `1.5px solid ${MOSS}55`, display: 'flex', alignItems: 'center', gap: '14px', minWidth: '360px',
      }}>
        <div style={{
          width: '40px', height: '40px', borderRadius: '50%', background: `${MOSS}18`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <CheckCircle2 size={22} strokeWidth={2.25} color={MOSS} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: INK, marginBottom: '2px' }}>Production started</div>
          <div style={{ fontSize: '13px', color: STONE }}>Job #{job} — {customer}</div>
        </div>
      </div>
      <style>{`@keyframes twpShopSlideDown { from { transform: translate(-50%, -100%); opacity: 0; } to { transform: translateX(-50%); opacity: 1; } }`}</style>
    </div>
  );
}

function ArchiveJobCard({ job, navigate }) {
  const accent = statusColor(job.status);
  return (
    <div onClick={() => navigate(`/shop/job/${job.id}`)} style={{
      background: '#fff', borderRadius: '14px', padding: '16px 18px', marginBottom: '10px',
      boxShadow: '0 2px 8px rgba(43,38,32,0.05)', border: `1px solid ${LINE}`,
      cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: FONT_SANS,
    }}>
      <div>
        <div style={{ fontSize: '16px', fontWeight: '700', color: INK, marginBottom: '4px' }}>
          #{job.contractNumber || job.id?.slice(0, 6)}
        </div>
        <div style={{ fontSize: '14px', fontWeight: '600', color: STONE, marginBottom: '4px' }}>
          {job.customerName || job.name || 'Customer'}
        </div>
        {(job.city || job.state) && (
          <div style={{ fontSize: '12.5px', color: STONE }}>{[job.city, job.state].filter(Boolean).join(', ')}</div>
        )}
      </div>
      <div style={{
        padding: '6px 12px', borderRadius: '10px', fontSize: '12px', fontWeight: '700',
        background: accent + '18', color: accent, border: `1px solid ${accent}33`, whiteSpace: 'nowrap'
      }}>
        {statusLabel(job.status)}
      </div>
    </div>
  );
}

export default function ShopPortalScreen() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { queueJobs, activeJobs, archiveJobs, loading, startProduction } = useShop();

  const [showSplash, setShowSplash] = useState(() => {
    return !sessionStorage.getItem('hasSeenShopSplash');
  });

  const [activeTab, setActiveTab] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [confirmJob, setConfirmJob] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const handleSplashComplete = () => {
    sessionStorage.setItem('hasSeenShopSplash', 'true');
    setShowSplash(false);
  };

  const handleSearch = (searchValue) => {
    setSearchQuery(searchValue);

    if (!searchValue.trim()) {
      setSearchResults([]);
      return;
    }

    const searchLower = searchValue.toLowerCase();
    const allJobs = [...activeJobs, ...queueJobs, ...archiveJobs];

    const results = allJobs
      .filter(job => {
        return (
          job.contractNumber?.toLowerCase().includes(searchLower) ||
          job.customerName?.toLowerCase().includes(searchLower) ||
          job.name?.toLowerCase().includes(searchLower) ||
          job.id?.toLowerCase().includes(searchLower)
        );
      })
      .slice(0, 10);

    setSearchResults(results);
  };

  const handleStartProduction = (job) => {
    setConfirmJob(job);
  };

  const confirmStartProduction = async () => {
    const job = confirmJob;
    setConfirmJob(null);

    try {
      const shopWorkerName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Shop Worker';
      await startProduction(job.id, shopWorkerName);
      setActiveTab('active');
      setSuccessMessage({
        job: job.contractNumber || job.id?.slice(0, 6),
        customer: job.customerName || job.name || 'Customer'
      });
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (error) {
      console.error('Error starting production:', error);
      alert('Failed to start production. Please try again or contact a manager.');
    }
  };

  if (showSplash) {
    return <ShopSplashScreen user={currentUser} onComplete={handleSplashComplete} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: PAPER, paddingBottom: '120px', position: 'relative', fontFamily: FONT_SANS }}>
      <ShopTopBar />

      <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        {/* Search */}
        <div style={{ marginBottom: '20px', position: 'relative' }}>
          <Search size={17} strokeWidth={2.25} color={STONE} style={{ position: 'absolute', left: '18px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
          <input
            type="text"
            placeholder="Search by contract # or customer name..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            style={{
              width: '100%', padding: '15px 20px 15px 44px', fontSize: '15px', fontFamily: FONT_SANS,
              border: `1.5px solid ${LINE}`, borderRadius: '14px', outline: 'none',
              background: '#fff', boxShadow: '0 2px 8px rgba(43,38,32,0.05)', boxSizing: 'border-box'
            }}
          />

          {searchQuery && searchResults.length > 0 && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0,
              background: '#fff', borderRadius: '12px', boxShadow: '0 8px 24px rgba(43,38,32,0.15)',
              border: `1px solid ${LINE}`, maxHeight: '400px', overflow: 'auto', zIndex: 100
            }}>
              {searchResults.map(job => (
                <div
                  key={job.id}
                  onClick={() => { setSearchQuery(''); setSearchResults([]); navigate(`/shop/job/${job.id}`); }}
                  style={{ padding: '14px 18px', borderBottom: `1px solid ${LINE}`, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: INK, marginBottom: '3px' }}>#{job.contractNumber || job.id?.slice(0, 6)}</div>
                    <div style={{ fontSize: '13px', color: STONE }}>{job.customerName || job.name || 'Customer'}</div>
                  </div>
                  <div style={{ padding: '4px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: '700', background: `${WALNUT}14`, color: WALNUT }}>
                    {statusLabel(job.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Stats */}
        <div style={{
          background: '#fff', borderRadius: '18px', padding: '20px', marginBottom: '20px',
          boxShadow: '0 4px 12px rgba(43,38,32,0.06)', border: `1px solid ${LINE}`
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
            {[
              { label: 'Active', value: activeJobs.length, accent: WALNUT },
              { label: 'In Queue', value: queueJobs.length, accent: OCHRE },
              { label: 'Archived', value: archiveJobs.length, accent: MOSS },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center', padding: '14px', background: `${s.accent}0d`, borderRadius: '12px', border: `1px solid ${s.accent}2b` }}>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: '28px', fontWeight: '600', color: s.accent, marginBottom: '2px' }}>{s.value}</div>
                <div style={{ fontSize: '11.5px', fontWeight: '600', color: STONE }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Active Tab */}
        {activeTab === 'active' && (
          <div>
            <SectionHeader Icon={Hammer} title="Active Production" count={activeJobs.length} countLabel="in progress" accent={WALNUT} />
            {loading ? (
              <EmptyState Icon={Clock} title="Loading jobs…" subtitle="Just a moment" />
            ) : activeJobs.length === 0 ? (
              <EmptyState Icon={Hammer} title="Nothing active" subtitle="Start a job from the Queue tab" />
            ) : (
              activeJobs.map(job => <ActiveJobCard key={job.id} job={job} />)
            )}
          </div>
        )}

        {/* Queue Tab */}
        {activeTab === 'queue' && (
          <div>
            <SectionHeader Icon={Clock} title="Production Queue" count={queueJobs.length} countLabel="waiting" accent={OCHRE} />
            {loading ? (
              <EmptyState Icon={Clock} title="Loading jobs…" subtitle="Just a moment" />
            ) : queueJobs.length === 0 ? (
              <EmptyState Icon={Inbox} title="All clear" subtitle="No jobs waiting for production" />
            ) : (
              queueJobs.map(job => <JobQueueCard key={job.id} job={job} onStartProduction={handleStartProduction} />)
            )}
          </div>
        )}

        {/* Archive Tab */}
        {activeTab === 'archive' && (
          <div>
            <SectionHeader Icon={Archive} title="Archived Jobs" count={archiveJobs.length} countLabel="completed" accent={MOSS} />
            {loading ? (
              <EmptyState Icon={Clock} title="Loading jobs…" subtitle="Just a moment" />
            ) : archiveJobs.length === 0 ? (
              <EmptyState Icon={Archive} title="No archived jobs" subtitle="Completed and ready-to-install jobs will appear here" />
            ) : (
              archiveJobs.map(job => <ArchiveJobCard key={job.id} job={job} navigate={navigate} />)
            )}
          </div>
        )}
      </div>

      <ShopBottomNav activeTab={activeTab} onTabChange={setActiveTab} />

      {confirmJob && <ConfirmationModal job={confirmJob} onConfirm={confirmStartProduction} onCancel={() => setConfirmJob(null)} />}
      {successMessage && <SuccessModal job={successMessage.job} customer={successMessage.customer} />}
    </div>
  );
}
